import type { AgendaItem } from "@elation/api-client/src/stitch-web";

import {
  ensureDroppableAddedByYouGroup,
  flattenConcernGroups,
  groupByConcern,
} from "@src/features/agenda/domain";

export const AGENDA_ITEM_DRAG_TYPE = "agenda-item";
export const CONCERN_GROUP_DRAG_TYPE = "concern-group";

/**
 * Sortable drag source fields from `@dnd-kit/react` v0.5.
 * Projected moves live on `initialIndex` → `index`; target id is not authoritative.
 */
export type AgendaGroupedDragSource = {
  id: string | number | symbol;
  type?: unknown;
  initialIndex: number;
  index: number;
  group?: string | number | null;
  originalGroupKey?: string | null;
};

function sameItemIdOrder(
  left: readonly AgendaItem[],
  right: readonly AgendaItem[],
): boolean {
  return (
    left.length === right.length &&
    left.every((item, index) => item.id === right[index]?.id)
  );
}

function moveIndex<T>(
  items: readonly T[],
  from: number,
  to: number,
): T[] | null {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= items.length ||
    to >= items.length
  ) {
    return null;
  }

  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return null;
  next.splice(to, 0, moved);
  return next;
}

/**
 * Result of resolving a grouped-agenda drag-end.
 *
 * `items` is always the full next flat order — safe to hand straight to a
 * reorder call. `concernChange` is set only when a custom item crossed into
 * a different concern group: the caller must PATCH `healthConcernId` for
 * `concernChange.itemId` (this is a group-membership change, not something
 * the `reorder` endpoint can express — it only accepts a permutation of
 * existing ids). `items` already reflects the item's new `healthConcernId`/
 * `healthConcern` so the caller can apply it as a single optimistic cache
 * write covering both group and position — writing them separately would
 * make the item visibly jump once the second write lands.
 */
export type AgendaGroupedDragResolution = {
  items: AgendaItem[];
  concernChange: { itemId: number; healthConcernId: number | null } | null;
};

function clampIndex(index: number, length: number): number {
  return Math.min(Math.max(index, 0), length);
}

function resolveItemReorder(
  items: readonly AgendaItem[],
  source: AgendaGroupedDragSource,
): AgendaGroupedDragResolution | null {
  const originalGroupKey = source.originalGroupKey;
  if (originalGroupKey == null) return null;

  const projectedGroupKey = source.group == null ? null : String(source.group);
  if (projectedGroupKey == null) return null;

  const groups = ensureDroppableAddedByYouGroup(groupByConcern(items), items);
  const fromGroupIndex = groups.findIndex(
    (group) => group.key === originalGroupKey,
  );
  if (fromGroupIndex < 0) return null;
  const fromGroup = groups[fromGroupIndex]!;

  if (projectedGroupKey === originalGroupKey) {
    const nextGroupItems = moveIndex(
      fromGroup.items,
      source.initialIndex,
      source.index,
    );
    if (!nextGroupItems) return null;

    const nextGroups = groups.map((entry, index) =>
      index === fromGroupIndex ? { ...entry, items: nextGroupItems } : entry,
    );
    const next = flattenConcernGroups(nextGroups);
    if (sameItemIdOrder(items, next)) return null;
    return { items: next, concernChange: null };
  }

  // Cross-group: only a custom item (no opportunity link) may move between
  // concern groups this way. Opportunity-linked items derive their concern
  // through the opportunity, and the server rejects a direct healthConcernId
  // write for them (`agenda_item.concern_not_assignable` in
  // agenda-item.entity.ts) — refusing here too avoids a doomed PATCH.
  const draggedItem = fromGroup.items.find(
    (entry) => String(entry.id) === String(source.id),
  );
  if (!draggedItem || draggedItem.careProtocolOpportunityId != null) {
    return null;
  }

  const toGroupIndex = groups.findIndex(
    (group) => group.key === projectedGroupKey,
  );
  if (toGroupIndex < 0) return null;
  const toGroup = groups[toGroupIndex]!;

  const fromIndex = fromGroup.items.indexOf(draggedItem);
  const nextFromItems = [...fromGroup.items];
  nextFromItems.splice(fromIndex, 1);

  // Borrow the target group's existing concern summary (name AND medical
  // codes) so the moved item — and, if it lands first, the group header that
  // reads both fields off `group.items[0]` (see `toAgendaConcernHeader`) —
  // display correctly before the server round-trip re-enriches it. Borrowing
  // only `healthConcern` and leaving `medicalCodes` empty would blank the
  // group's code chips whenever the moved item becomes the group's first
  // item.
  const donorItem =
    toGroup.healthConcernId == null
      ? undefined
      : toGroup.items.find((entry) => entry.healthConcern != null);

  const movedItem: AgendaItem = {
    ...draggedItem,
    healthConcernId: toGroup.healthConcernId,
    healthConcern: donorItem?.healthConcern ?? null,
    medicalCodes: donorItem?.medicalCodes ?? [],
  };

  const nextToItems = [...toGroup.items];
  nextToItems.splice(
    clampIndex(source.index, nextToItems.length),
    0,
    movedItem,
  );

  const nextGroups = groups.map((entry, index) => {
    if (index === fromGroupIndex) return { ...entry, items: nextFromItems };
    if (index === toGroupIndex) return { ...entry, items: nextToItems };
    return entry;
  });

  // Unlike the same-group and whole-group-reorder paths, a flat-array no-op
  // check doesn't apply here: `fromGroupIndex !== toGroupIndex` already
  // guarantees a real change (the item's concern fields differ even when its
  // position in the flattened array happens to be unchanged — e.g. it sat
  // right after the target group already).
  const next = flattenConcernGroups(nextGroups);

  return {
    items: next,
    concernChange: {
      itemId: movedItem.id,
      healthConcernId: toGroup.healthConcernId,
    },
  };
}

function resolveConcernGroupReorder(
  items: readonly AgendaItem[],
  source: AgendaGroupedDragSource,
): AgendaGroupedDragResolution | null {
  const groups = groupByConcern(items);
  const nextGroupOrder = moveIndex(groups, source.initialIndex, source.index);
  if (!nextGroupOrder) return null;

  const next = flattenConcernGroups(nextGroupOrder);
  if (sameItemIdOrder(items, next)) return null;
  return { items: next, concernChange: null };
}

/**
 * Adapt a `@dnd-kit` drag source into the sortable fields this resolver needs.
 */
export function toAgendaGroupedDragSource(
  source:
    | {
        id: string | number | symbol;
        type?: unknown;
        initialIndex?: number;
        index?: number;
        group?: string | number | null;
        data?: { groupKey?: string } | null;
      }
    | null
    | undefined,
): AgendaGroupedDragSource | null {
  if (
    !source ||
    typeof source.initialIndex !== "number" ||
    typeof source.index !== "number"
  ) {
    return null;
  }

  return {
    id: source.id,
    type: source.type,
    initialIndex: source.initialIndex,
    index: source.index,
    group: source.group,
    originalGroupKey: source.data?.groupKey ?? null,
  };
}

/**
 * Resolve a grouped-agenda drag-end into the next flat item order (plus a
 * concern-change signal when a custom item crossed groups).
 * Returns null when the move is canceled, refused (cross-concern move of an
 * opportunity-linked item), or a no-op.
 */
export function resolveAgendaGroupedDragEnd(
  items: readonly AgendaItem[],
  source: AgendaGroupedDragSource | null | undefined,
): AgendaGroupedDragResolution | null {
  if (!source) return null;
  if (
    typeof source.initialIndex !== "number" ||
    typeof source.index !== "number" ||
    !Number.isFinite(source.initialIndex) ||
    !Number.isFinite(source.index)
  ) {
    return null;
  }

  if (source.type === AGENDA_ITEM_DRAG_TYPE) {
    return resolveItemReorder(items, source);
  }

  if (source.type === CONCERN_GROUP_DRAG_TYPE) {
    return resolveConcernGroupReorder(items, source);
  }

  return null;
}
